// bill_subsystem_v1 + invoice_image_v1: render invoice as image + preview + send
import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import API from '../api';

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(s) {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return s; }
}

export default function InvoiceImageModal(props) {
  const invoice = props.invoice;
  const items = props.items || [];
  const onClose = props.onClose;

  const billRef = useRef(null);
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [rendering, setRendering] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Render after mount
  useEffect(function () {
    setTimeout(function () { captureImage(); }, 300); /* let DOM settle */
  }, []);

  async function captureImage() {
    if (!billRef.current) return;
    setRendering(true);
    try {
      var canvas = await html2canvas(billRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      setImageDataUrl(canvas.toDataURL('image/png'));
    } catch (e) {
      setError('สร้างรูปไม่สำเร็จ: ' + e.message);
    } finally {
      setRendering(false);
    }
  }

  function handleDownload() {
    if (!imageDataUrl) return;
    var link = document.createElement('a');
    link.href = imageDataUrl;
    link.download = 'invoice-' + (invoice.invoice_number || invoice.id) + '.png';
    link.click();
  }

  async function handleSend(channel) {
    if (!imageDataUrl) { setError('รูปยังไม่พร้อม'); return; }
    if (!invoice.tenant_customer_id) { setError('ใบนี้ยังไม่ผูกลูกค้า'); return; }
    setError('');
    setSuccess('');
    setSending(true);
    try {
      /* upload_first_v1: upload image to disk first, then send with URL */
      var uploadRes = await API.post('/api/invoices/' + invoice.id + '/upload-image', {
        image_base64: imageDataUrl.replace(/^data:image\/png;base64,/, ''),
      });
      var imageUrl = uploadRes.data && uploadRes.data.url;
      if (!imageUrl) { setError('Upload image failed'); setSending(false); return; }
      var res = await API.post('/api/invoices/' + invoice.id + '/send-to-customer', {
        customer_id: invoice.tenant_customer_id,
        channel: channel,
        custom_message: null,
        image_url: imageUrl,
      });
      setSuccess('✅ ส่งสำเร็จผ่าน ' + (channel === 'telegram' ? 'Telegram' : 'LINE'));
      setTimeout(function () { onClose(true); }, 1500);
    } catch (e) {
      var errData = e.response && e.response.data;
      var msg = (errData && errData.error) || 'ส่งไม่สำเร็จ';
      if (errData && errData.detail) msg += ' (' + errData.detail + ')';
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <div onClick={function () { if (!sending) onClose(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, zIndex: 100, overflowY: 'auto' }}>
      <div onClick={function (e) { e.stopPropagation(); }} style={{ background: 'white', borderRadius: 14, padding: 20, maxWidth: 600, width: '100%', margin: '20px auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>🖼️ ตัวอย่างบิล {invoice.invoice_number}</h3>
          <button onClick={function () { if (!sending) onClose(false); }} disabled={sending} style={{ border: 'none', background: 'transparent', cursor: sending ? 'not-allowed' : 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* The "template" — what gets snapshot. Hidden in flow but in DOM. */}
        <div style={{ position: 'absolute', left: -9999, top: 0, width: 600 }}>
          <div ref={billRef} style={{ background: 'white', padding: 30, fontFamily: 'sans-serif', color: '#1e293b', width: 540 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, color: '#10b981' }}>ใบแจ้งหนี้ / INVOICE</h2>
              <div style={{ fontSize: 14, color: '#64748b' }}>{invoice.invoice_number}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>เรียน:</div>
                <div>{invoice.customer_name}</div>
                {invoice.customer_phone && <div>โทร: {invoice.customer_phone}</div>}
                {invoice.customer_address && <div style={{ fontSize: 12, color: '#64748b' }}>{invoice.customer_address}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div>วันที่ออก: {fmtDate(invoice.issue_date)}</div>
                {invoice.due_date && <div>ครบกำหนด: {fmtDate(invoice.due_date)}</div>}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 16 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: 8, textAlign: 'left', border: '1px solid #e2e8f0' }}>รายการ</th>
                  <th style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0', width: 60 }}>จำนวน</th>
                  <th style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0', width: 80 }}>ราคา</th>
                  <th style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0', width: 90 }}>รวม</th>
                </tr>
              </thead>
              <tbody>
                {items.map(function (it, idx) {
                  return (
                    <tr key={idx}>
                      <td style={{ padding: 8, border: '1px solid #e2e8f0' }}>
                        {it.description}
                        {Number(it.reference_amount || 0) > 0 && (
                          <div style={{ fontSize: 10, color: '#64748b' }}>(จากยอด ฿{fmtMoney(it.reference_amount)})</div>
                        )}
                      </td>
                      <td style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0' }}>{Number(it.quantity)}</td>
                      <td style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0' }}>{fmtMoney(it.unit_price)}</td>
                      <td style={{ padding: 8, textAlign: 'right', border: '1px solid #e2e8f0' }}>{fmtMoney(it.line_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#ecfdf5' }}>
                  <td colSpan={3} style={{ padding: 8, textAlign: 'right', fontWeight: 700, border: '1px solid #e2e8f0' }}>รวมทั้งสิ้น</td>
                  <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#047857', border: '1px solid #e2e8f0' }}>฿{fmtMoney(invoice.total_amount)}</td>
                </tr>
              </tfoot>
            </table>

            {invoice.payment_method && (
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                💳 วิธีชำระ: {invoice.payment_method}
              </div>
            )}

            {invoice.note && (
              <div style={{ fontSize: 11, color: '#64748b', borderTop: '1px dashed #cbd5e1', paddingTop: 8, marginTop: 12 }}>
                {invoice.note}
              </div>
            )}

            <div style={{ marginTop: 20, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
              ── ขอบคุณที่ใช้บริการ ──
            </div>
          </div>
        </div>

        {/* Preview area */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 12, minHeight: 200 }}>
          {rendering && <div style={{ textAlign: 'center', color: '#94a3b8' }}>กำลังสร้างรูป...</div>}
          {!rendering && imageDataUrl && (
            <img src={imageDataUrl} alt="invoice preview" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
          )}
        </div>

        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
        {success && <div style={{ background: '#dcfce7', color: '#15803d', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10, fontWeight: 600 }}>{success}</div>}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={handleDownload} disabled={!imageDataUrl || sending} style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: (!imageDataUrl || sending) ? 'not-allowed' : 'pointer', fontSize: 13 }}>📥 บันทึก PNG</button>
          <button onClick={function () { handleSend('telegram'); }} disabled={!imageDataUrl || sending} style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: 8, border: 'none', background: sending ? '#94a3b8' : '#0088cc', color: 'white', cursor: (!imageDataUrl || sending) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>📤 Telegram</button>
          <button onClick={function () { handleSend('line'); }} disabled={!imageDataUrl || sending} style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: 8, border: 'none', background: sending ? '#94a3b8' : '#06c755', color: 'white', cursor: (!imageDataUrl || sending) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>📤 LINE</button>
        </div>

        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
          กดส่งจะ mark สถานะเป็น "ส่งแล้ว" อัตโนมัติ
        </div>
      </div>
    </div>
  );
}
